import { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './ArtworkCard.module.css';
import { api, endpoints } from '../apiConfig';

const ArtworkCard = ({ artwork, onEdit, onDelete, onPublish, onUpdateStatus, onUpdateSoldStatus, onView, onShare }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleView = (e) => {
    e.preventDefault();
    onView(artwork);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    onEdit(artwork);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setIsDeleting(true);
  };

  const handleConfirmDelete = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onDelete(artwork);
    } catch (error) {
      console.error('Error deleting artwork:', error);
    } finally {
      setIsLoading(false);
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = (e) => {
    e.preventDefault();
    setIsDeleting(false);
  };

  const isSold = Boolean(artwork.is_sold) || artwork.status === 'sold';

  const handlePublish = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onPublish(artwork);
    } catch (error) {
      console.error('Error publishing artwork:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSoldStatus = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onUpdateSoldStatus(artwork, !artwork.is_sold);
    } catch (error) {
      console.error('Error updating artwork sold status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setIsAnalyzing(true);
    try {
      const response = await api.post(endpoints.artwork.analyze(artwork.id));
      // Update the artwork in parent component
      onEdit(response.data.artwork);
    } catch (error) {
      console.error('Error analyzing artwork:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    if (onShare) {
      onShare(artwork);
    } else {
      // Create share text
      const shareText = `Check out "${artwork.title}" - ${artwork.medium}, ${artwork.dimensions.height}" × ${artwork.dimensions.width}"${artwork.dimensions.depth > 0 ? ` × ${artwork.dimensions.depth}"` : ''}, ${artwork.year_created}`;
      
      // If native sharing is available, use it
      if (navigator.share) {
        navigator.share({
          title: artwork.title,
          text: shareText,
          url: window.location.href
        }).catch(console.error);
      } else {
        // Otherwise copy to clipboard
        navigator.clipboard.writeText(`${shareText}\n${window.location.href}`).then(() => {
          // Show temporary success message
          const button = e.currentTarget;
          const originalText = button.innerHTML;
          button.innerHTML = '<span class="' + styles.actionIcon + '">✓</span>';
          setTimeout(() => {
            button.innerHTML = originalText;
          }, 2000);
        }).catch(console.error);
      }
    }
  };

  const formatStatus = (artwork) => {
    if (artwork.is_sold) return 'Sold';
    if (artwork.status === 'draft') return 'Draft';
    if (artwork.status === 'published') return 'Published';
    return 'Available';
  };

  const getStatusClass = (artwork) => {
    if (artwork.is_sold) return styles.statusSold;
    if (artwork.status === 'draft') return styles.statusDraft;
    if (artwork.status === 'published') return styles.statusPublished;
    return styles.statusAvailable;
  };

  const getAspectRatio = (artwork) => {
    if (!artwork.dimensions) return '4:3'; // default
    const { width, height } = artwork.dimensions;
    if (!width || !height) return '4:3';
    
    // Calculate ratio and round to nearest standard ratio
    const ratio = width / height;
    if (ratio > 0.9 && ratio < 1.1) return '1:1';
    if (ratio > 0.7 && ratio < 0.85) return '3:4';
    return '4:3';
  };

  return (
    <div className={styles.card}>
      <div 
        className={styles.imageContainer} 
        onClick={handleView} 
        role="button" 
        tabIndex={0}
        data-aspect-ratio={getAspectRatio(artwork)}
      >
        <img src={artwork.image_url} alt={artwork.title} className={styles.image} />
        <div className={`${styles.statusBadge} ${getStatusClass(artwork)}`}>
          {formatStatus(artwork)}
        </div>
        
        {/* Quick Actions */}
        <div className={styles.quickActions}>
          <button 
            className={styles.actionButton} 
            onClick={handleShare}
            aria-label="Share artwork"
            title="Share artwork"
          >
            <span className={styles.actionIcon}>↗️</span>
          </button>
        </div>

        {artwork.detail_images?.length > 0 && (
          <div className={styles.zoomIndicator}>
            <span className={styles.zoomIcon}>🔍+</span>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.artworkInfo}>
          <h3 className={styles.title}>{artwork.title}</h3>
          {artwork.dimensions && (
            <div className={styles.dimensions}>
              {artwork.dimensions.height}" × {artwork.dimensions.width}"{artwork.dimensions.depth > 0 ? ` × ${artwork.dimensions.depth}"` : ''} ({artwork.dimensions.unit})
            </div>
          )}
          <div className={styles.primaryMetadata}>
            <span className={styles.medium}>{artwork.medium}</span>
            <span className={styles.price}>${artwork.price}</span>
          </div>
        </div>

        <div className={styles.actions}>
          {isDeleting ? (
            <>
              <button 
                className={styles.buttonDanger}
                onClick={handleConfirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button 
                className={styles.buttonSecondary}
                onClick={handleCancelDelete}
                disabled={isLoading}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button 
                className={styles.buttonSecondary}
                onClick={handleEdit}
                disabled={isLoading}
              >
                Edit
              </button>

              {artwork.status === 'draft' && (
                <button 
                  className={styles.buttonPublish}
                  onClick={handlePublish}
                  disabled={isLoading}
                >
                  {isLoading ? 'Publishing...' : 'Ready to Publish'}
                </button>
              )}

              {artwork.status === 'published' && (
                <button 
                  className={styles.buttonPrimary}
                  onClick={handleUpdateSoldStatus}
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : artwork.is_sold ? 'Mark as Available' : 'Mark as Sold'}
                </button>
              )}

              {!isSold && (
                <button
                  className={styles.buttonDanger}
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
        {isSold && (
          <p className={styles.soldNote}>
            This piece has sold, so it stays in your account. The storefront listing is already removed.
          </p>
        )}
      </div>
    </div>
  );
};

ArtworkCard.propTypes = {
  artwork: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    image_url: PropTypes.string.isRequired,
    medium: PropTypes.string,
    dimensions: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      depth: PropTypes.number,
      unit: PropTypes.string
    }),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string.isRequired,
    is_sold: PropTypes.bool,
    status: PropTypes.string,
    ai_description: PropTypes.string,
    color_palette: PropTypes.arrayOf(PropTypes.string),
    price_analysis: PropTypes.string,
    detail_images: PropTypes.arrayOf(PropTypes.string),
    similar_works_count: PropTypes.number,
    edition: PropTypes.shape({
      number: PropTypes.number,
      size: PropTypes.number,
    }),
    condition: PropTypes.string,
    authentication_status: PropTypes.string,
    exhibition_history: PropTypes.string,
    market_analysis: PropTypes.shape({
      market_position: PropTypes.string,
      price_trend: PropTypes.string,
      stats: PropTypes.shape({
        average: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
        count: PropTypes.number,
      }),
      recommendation: PropTypes.string,
      confidence_score: PropTypes.number,
    }),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onUpdateStatus: PropTypes.func.isRequired,
  onUpdateSoldStatus: PropTypes.func,
  onView: PropTypes.func.isRequired,
  onShare: PropTypes.func,
};

export default ArtworkCard; 